/**
 * RoofScreenshotCapture renderer · iter303ai-fix8
 *
 * Wraps the existing `RoofDiagramPicker` component so operators can upload
 * a Google-Earth screenshot of the property roof directly inside the
 * Workflow Runner (Appraisal Booked · Schedule Appraisal task).
 *
 * The picker writes to `property.roof_screenshot_photo_id` under the hood
 * (via PATCH /properties/{property_id}). The Continue button is gated
 * until that field is populated on the property record.
 *
 * capturedData.property_id must be set (seeded by the Client Data walk-
 * through OR by the console when it starts a run against an appraisal).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Image as ImageIcon, ExternalLink } from 'lucide-react';
import RoofDiagramPicker from '@/components/RoofDiagramPicker';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function RoofScreenshotCapture({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const params = action.params || {};
  const propertyId = capturedData?.property_id;
  const [prop, setProp] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}`);
      setProp(data);
      // iter303ak-fix2 · Fetch the photo URL so we can render the actual
      // screenshot image instead of just the ID hash. Falls back silently
      // if the photo record was deleted while the ID lingers on the prop.
      const pid = data?.roof_screenshot_photo_id || data?.roof_diagram_photo_id;
      if (pid) {
        try {
          const { data: photo } = await api.get(`/photos/by-id/${pid}`);
          setPhotoUrl(photo?.url ? `${process.env.REACT_APP_BACKEND_URL}${photo.url}` : '');
        } catch { setPhotoUrl(''); }
      } else {
        setPhotoUrl('');
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const persist = async (photoId) => {
    setSaving(true);
    try {
      await api.patch(`/properties/${propertyId}`, { roof_screenshot_photo_id: photoId });
      await load();
      setPickerOpen(false);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save roof screenshot');
    } finally { setSaving(false); }
  };

  const currentPhotoId = prop?.roof_screenshot_photo_id || prop?.roof_diagram_photo_id || '';
  const hasScreenshot = !!currentPhotoId;
  // HOTFIX 2026-09-14 (Audit D P1): properties use property_address_num/street/suburb/city
  // NOT address_line/suburb/city → Google Earth link was going generic instead of to the
  // actual property.
  const _addrLine = [prop?.property_address_num, prop?.property_address_street].filter(Boolean).join(' ').trim();
  const _addrFull = [_addrLine, prop?.property_address_suburb, prop?.property_address_city].filter(Boolean).join(', ');
  const googleEarthUrl = _addrFull
    ? `https://earth.google.com/web/search/${encodeURIComponent(_addrFull)}`
    : 'https://earth.google.com/web/';

  if (!propertyId) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
        No property linked to this run — cannot capture a roof screenshot.
        {canBack && (
          <button onClick={onBack} className="ml-3 text-rose-800 underline">← Back</button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="wf-roof-screenshot-capture">
      <div className="bg-gradient-to-br from-orange-50 to-white border-l-4 border-orange-400 rounded-r-lg p-6 mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-orange-600 font-semibold mb-2">
          <ImageIcon size={12} /> Roof Screenshot Capture
        </div>
        <div className="text-lg text-slate-800 leading-relaxed mb-3">
          {params.prompt || "Grab a Google Earth aerial screenshot of the property's roof and upload it here."}
        </div>
        <a
          href={googleEarthUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="wf-open-google-earth"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-700 hover:text-orange-900 underline decoration-dotted">
          <ExternalLink size={13} /> Open Google Earth at {_addrLine || 'this property'}
        </a>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-slate-400 italic">Loading property…</div>
      ) : (
        <div className="border-2 border-slate-200 rounded-lg overflow-hidden bg-white mb-6">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">
                {hasScreenshot ? 'Screenshot on file' : 'No screenshot yet'}
              </div>
              <div className="text-xs text-slate-500">
                {_addrLine || ''} {prop?.property_address_suburb ? `· ${prop.property_address_suburb}` : ''} {prop?.property_address_city ? `· ${prop.property_address_city}` : ''}
              </div>
            </div>
            <button
              onClick={() => setPickerOpen(o => !o)}
              data-testid="wf-toggle-screenshot-picker"
              className="px-3 py-1.5 bg-orange-600 text-white text-xs font-semibold rounded hover:bg-orange-700">
              {pickerOpen ? 'Close picker' : (hasScreenshot ? 'Change screenshot' : '+ Add Roof Screenshot')}
            </button>
          </div>

          {pickerOpen && (
            <div className="p-4 bg-slate-50">
              <RoofDiagramPicker
                api={api}
                propertyId={propertyId}
                selectedId={currentPhotoId}
                onSelect={persist}
                onClose={() => setPickerOpen(false)}
              />
              {saving && <div className="mt-2 text-xs text-slate-500 italic">Saving…</div>}
            </div>
          )}

          {!pickerOpen && hasScreenshot && prop && (
            <div className="p-4">
              {/* iter303ak-fix2 · Actually render the screenshot image
                  inline. Previously we only showed the ID hash which was
                  useless for the operator sanity-checking on-site. */}
              <div className="flex items-start gap-4">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt="Roof screenshot"
                    className="w-64 h-48 object-cover rounded border border-slate-200 shadow-sm bg-slate-50"
                    data-testid="wf-roof-screenshot-preview"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-64 h-48 rounded border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-400">
                    Loading preview…
                  </div>
                )}
                <div className="flex-1 text-xs text-slate-500">
                  <div className="mb-1"><b className="text-slate-700">Screenshot on file</b></div>
                  <div>ID: <span className="font-mono">{currentPhotoId.slice(0, 12)}…</span> ✓</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <button
          onClick={onNext}
          disabled={!hasScreenshot || saving}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">
          {hasScreenshot ? 'Save & Continue →' : 'Upload a screenshot first'}
        </button>
      </div>
    </div>
  );
}
